import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

const AppContext = createContext(undefined);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { product: [], warehouse: [] };

export const AppContextProvider = ({ children }) => {
  const { token, logout, user } = useAuth();
  const [appContext, setAppContext] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const username = user?.username || '';

  useEffect(() => {
    if (!token) {
      setAppContext(EMPTY);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/app/context`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status === 401) logout();
          throw new Error('Failed to fetch context');
        }

        setAppContext(await response.json());
      } catch (err) {
        if (err.name !== 'AbortError' && !controller.signal.aborted) {
          console.error('Failed to fetch app context:', err);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [token, logout, username]);

  const refreshContext = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/v1/app/context`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) setAppContext(await response.json());
    } catch (err) {
      console.error('Failed to refresh app context:', err);
    }
  }, [token]);

  const value = useMemo(() => ({
    appContext, setAppContext, refreshContext, loading
  }), [appContext, refreshContext, loading]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppContextProvider');
  }
  return context;
};
